// CountingStream — live video embed from the counting hall.
//
// Reads the configured URL from /api/counting-stream and renders a
// 16:9 iframe. Empty URL = component renders NOTHING (so the slot is
// invisible when no stream is configured — no broken box).
//
// Platform handling: the editor pastes either a plain URL or a full
// <iframe> snippet at /admin/counting-stream. The admin endpoint
// extracts the src on save. For display, we pass the saved URL to a
// minimal platform-detection helper that normalises Facebook/YouTube/
// raw-iframe URLs into something an <iframe> can render. Anything
// unrecognised falls through unchanged — paste-an-iframe-src works
// out of the box.
//
// No scroll-mini behaviour (unlike LiveDebatePlayer): the stream sits
// inside the side-by-side live core, not the main scroll flow.

import { useEffect, useState } from 'react';
import { toFeedEmbedSrc } from '../../lib/feedEmbed';
import './CountingStream.css';

const ENDPOINT = '/api/counting-stream';

/** Normalise a saved URL to a value safe to drop into <iframe src>.
 *  Delegates to the shared toFeedEmbedSrc helper (Facebook/YouTube/
 *  Instagram/X/Twitter). Falls back to the raw URL when unrecognised
 *  — keeps the previous "paste an iframe src and it works" behaviour
 *  for editor-supplied widget URLs we don't pattern-match. */
function toStreamEmbedSrc(stored: string): string {
  const url = stored.trim();
  if (!url) return '';
  return toFeedEmbedSrc(url) ?? url;
}

export default function CountingStream() {
  const [url, setUrl] = useState<string>('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetch(ENDPOINT, { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : { url: '' }))
      .then((data: unknown) => {
        if (cancelled) return;
        const next =
          data && typeof data === 'object' && 'url' in data &&
          typeof (data as { url: unknown }).url === 'string'
            ? (data as { url: string }).url
            : '';
        setUrl(next);
      })
      .catch(() => {
        /* fail silently — render nothing on error */
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Don't render the slot at all if there's nothing to show.
  if (loading || !url) return null;
  const embedSrc = toStreamEmbedSrc(url);
  if (!embedSrc) return null;

  return (
    <section className="counting-stream" aria-label="Counting-hall live stream">
      <div className="counting-stream__frame">
        <iframe
          className="counting-stream__iframe"
          src={embedSrc}
          title="Counting-hall live stream"
          allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
          allowFullScreen
          scrolling="no"
        />
      </div>
      <div className="counting-stream__caption">
        <span className="counting-stream__live-dot" aria-hidden="true" />
        <span>Live · counting hall</span>
      </div>
    </section>
  );
}
